import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ErpContext } from "../../context/ErpContext.js";

const SeleccionLotesModal = ({
  open,
  onClose,
  item,
  idUbicacion,
  lotesIniciales,
  onConfirmar,
}) => {
  const { getLotesDisponiblesVenta } = useContext(ErpContext);

  const [todosLosLotes, setTodosLosLotes] = useState([]);
  const [lotesSeleccionados, setLotesSeleccionados] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const cantidadRequerida = item?.cantidad || 0;

  useEffect(() => {
    if (open && item) {
      cargarLotesDisponibles();
      inicializarSeleccion();
    }
  }, [open, item, lotesIniciales]);

  const cargarLotesDisponibles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const lotes = await getLotesDisponiblesVenta(item.idItem, idUbicacion);
      setTodosLosLotes(lotes);
    } catch (err) {
      console.error("Error al cargar lotes:", err);
      setError("Error al cargar lotes disponibles");
    } finally {
      setIsLoading(false);
    }
  };

  const inicializarSeleccion = () => {
    if (lotesIniciales && lotesIniciales.length > 0) {
      const seleccion = {};
      lotesIniciales.forEach((lote) => {
        seleccion[lote.idLote] = lote.cantidad;
      });
      setLotesSeleccionados(seleccion);
    }
  };

  const handleCheckboxChange = (idLote, stockDisponible) => {
    setLotesSeleccionados((prev) => {
      const newSelection = { ...prev };
      
      if (newSelection[idLote] !== undefined) {
        // Si ya está seleccionado, deseleccionar
        delete newSelection[idLote];
      } else {
        // Si no está seleccionado, sugerir el stock completo disponible
        // El usuario puede ajustar manualmente después
        newSelection[idLote] = stockDisponible;
      }
      
      return newSelection;
    });
  };

  const handleCantidadChange = (idLote, valor) => {
    const cantidad = parseFloat(valor);
    
    if (isNaN(cantidad) || cantidad < 0) {
      return;
    }

    const lote = todosLosLotes.find((l) => l.idLote === idLote);
    if (cantidad > lote.stockDisponible) {
      return;
    }

    setLotesSeleccionados((prev) => ({
      ...prev,
      [idLote]: cantidad,
    }));
  };

  const calcularTotalSeleccionado = () => {
    return Object.values(lotesSeleccionados).reduce((sum, cant) => sum + cant, 0);
  };

  const totalSeleccionado = calcularTotalSeleccionado();
  const esValido = Math.abs(totalSeleccionado - cantidadRequerida) < 0.01;
  const tieneFaltante = totalSeleccionado < cantidadRequerida;
  const tieneExceso = totalSeleccionado > cantidadRequerida;

  const handleConfirmar = () => {
    // 🆕 Ya no validamos si es válido, el usuario puede confirmar con cualquier cantidad
    // Es responsabilidad del usuario asegurarse de que la cantidad sea correcta
    
    const lotesParaEnviar = Object.entries(lotesSeleccionados)
      .filter(([_, cantidad]) => cantidad > 0)
      .map(([idLote, cantidad]) => {
        const lote = todosLosLotes.find((l) => l.idLote === parseInt(idLote));
        return {
          idLote: parseInt(idLote),
          numeroLote: lote.numeroLote,
          cantidad: cantidad,
          fechaVencimiento: lote.fechaVencimiento,
          fechaFabricacion: lote.fechaFabricacion,
          stockDisponible: lote.stockDisponible,
        };
      });

    onConfirmar(lotesParaEnviar);
    onClose();
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleDateString("es-AR");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6">Selección de Lotes</Typography>
          <Typography variant="caption" color="text.secondary">
            {item?.descripcion} - Cantidad a vender: {cantidadRequerida} unidades
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : todosLosLotes.length === 0 ? (
          <Alert severity="warning">No hay lotes disponibles para este producto</Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Número de Lote</TableCell>
                    <TableCell>Fecha Fab.</TableCell>
                    <TableCell>Fecha Venc.</TableCell>
                    <TableCell align="right">Stock Disponible</TableCell>
                    <TableCell align="right">Cantidad a Usar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todosLosLotes.map((lote) => {
                    const estaSeleccionado = lotesSeleccionados[lote.idLote] !== undefined;
                    const cantidadAsignada = lotesSeleccionados[lote.idLote] || 0;

                    return (
                      <TableRow key={lote.idLote} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={estaSeleccionado}
                            onChange={() => handleCheckboxChange(lote.idLote, lote.stockDisponible)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={estaSeleccionado ? 600 : 400}>
                            {lote.numeroLote}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatFecha(lote.fechaFabricacion)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatFecha(lote.fechaVencimiento)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{lote.stockDisponible.toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {estaSeleccionado ? (
                            <TextField
                              type="number"
                              size="small"
                              value={cantidadAsignada}
                              onChange={(e) => handleCantidadChange(lote.idLote, e.target.value)}
                              inputProps={{
                                min: 0,
                                max: lote.stockDisponible,
                                step: 0.01,
                              }}
                              sx={{ width: 100 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: esValido ? "#e8f5e9" : (tieneFaltante ? "#fff3e0" : "#ffebee"), 
              borderRadius: 1,
              border: esValido ? "2px solid #4caf50" : (tieneFaltante ? "2px solid #ff9800" : "2px solid #f44336")
            }}>
              <Typography variant="body1" fontWeight={600}>
                Total seleccionado: {totalSeleccionado.toFixed(2)} / {cantidadRequerida.toFixed(2)}{" "}
                {esValido ? "✓" : (tieneFaltante ? "⚠️" : "❌")}
              </Typography>
              {tieneFaltante && (
                <Typography variant="caption" sx={{ color: "#ed6c02", display: "block", mt: 0.5 }}>
                  ⚠️ Faltante: {(cantidadRequerida - totalSeleccionado).toFixed(2)} unidades
                </Typography>
              )}
              {tieneExceso && (
                <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}>
                  ❌ Exceso: {(totalSeleccionado - cantidadRequerida).toFixed(2)} unidades
                </Typography>
              )}
              {esValido && (
                <Typography variant="caption" sx={{ color: "#2e7d32", display: "block", mt: 0.5 }}>
                  ✓ Cantidad correcta
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmar} 
          variant="contained" 
          disabled={isLoading || Object.keys(lotesSeleccionados).length === 0}
          color={esValido ? "primary" : "warning"}
        >
          Confirmar Selección {!esValido && "(⚠️)"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeleccionLotesModal;

